import React from "react";
import Topo from "../anim/Topo";
import styles from "./Section.module.css";

/*
 * Section — the single layout primitive every marketing section is built on.
 *
 * It owns the things sections kept reinventing (and getting out of sync):
 *   1. the surface (background + on-surface text colours + contour texture)
 *   2. the vertical rhythm (padding-block, or a viewport-tall "screen")
 *   3. the max-width container + page gutters
 *
 * A section's own CSS module must therefore NEVER set background, padding-block
 * on the <section>, or a container max-width. It only styles what lives inside.
 *
 * Props
 *   tone       "paper" | "olive" | "void"            surface (default "paper")
 *              Legacy names still work while pages migrate:
 *                light | muted -> paper,  dark | deep -> olive
 *              data-tone on the <section> ALWAYS carries the canonical name —
 *              the Header reads [data-tone] to pick its own colour.
 *   size       "sm" | "std" | "lg" | "screen"        vertical rhythm (default "std")
 *              "screen" = at least one viewport tall (100svh, 100vh fallback)
 *              with the content vertically centred.
 *   panel      boolean — olive/void only. The dark surface becomes an INSET
 *              ROUNDED PANEL sitting on the page's paper (radius
 *              var(--radius-panel), 16px from the viewport, 8px <= 980px)
 *              instead of an edge-to-edge band. The panel clips its content to
 *              its radius. Ignored on paper. Not for sticky pin tracks.
 *   notch      boolean — with panel: the centred raised tab on the top edge.
 *   contour    boolean (default true) — the static topographic layer behind the
 *              content. Pass false when the section draws its own (the hero's
 *              cursor-reactive <Topo variant="hero" />).
 *   container  "narrow" | "base" | "wide" | "none"   inner width (default "base")
 *              "none" renders children full-bleed with no container wrapper —
 *              use it only for edge-to-edge marquees/pinned panels that manage
 *              their own inner container.
 *   id         anchor id on the <section>
 *   className  extra class on the <section> (layout hooks, not background/padding)
 *   innerRef   optional ref to the underlying <section> element
 *   ...rest    spread onto the <section> (style, aria-*, data-*, ...)
 *
 * Every Section exposes contextual custom properties to its subtree, so a
 * section module can stay tone-agnostic:
 *   --section-bg         this surface's colour
 *   --section-text       primary text colour for this surface
 *   --section-text-soft  readable secondary text colour for this surface
 *   --section-border     hairline colour for this surface
 *   --section-accent     serif accent-word colour (--ink on paper, --lime-ink
 *                        on olive/void — never full --lime for text)
 *   --focus-ring-color   --ink on paper, --lime on olive/void
 *   --section-pad-y      this section's vertical rhythm unit
 *   --section-inset      the panel inset (16px, 8px <= 980px)
 *
 * Nested surface hook: an element INSIDE a Section that paints a different
 * surface (a paper card in an olive section, a lime block, ...) sets
 * data-surface="paper|olive|void|lime" and gets the right --section-* values,
 * text colour and heading/paragraph colours for that surface. Never put
 * data-tone on a nested element — that attribute is the Header's signal.
 *
 * The Section is its own stacking context (isolation), so the contour layer can
 * sit behind the content without a wrapper. A position:fixed overlay rendered
 * inside a Section cannot rise above the Header — portal it to <body>.
 */

const TONE_ALIAS = {
  paper: "paper",
  olive: "olive",
  void: "void",
  // legacy names, kept so untouched pages keep working during the migration
  light: "paper",
  muted: "paper",
  dark: "olive",
  deep: "olive"
};

const TONE_CLASS = {
  paper: styles.tonePaper,
  olive: styles.toneOlive,
  void: styles.toneVoid
};

const SIZE_CLASS = {
  sm: styles.sizeSm,
  std: styles.sizeStd,
  lg: styles.sizeLg,
  screen: styles.sizeScreen
};

const CONTAINER_CLASS = {
  narrow: styles.containerNarrow,
  base: styles.containerBase,
  wide: styles.containerWide
};

function cx(...values) {
  return values.filter(Boolean).join(" ");
}

function keyOr(map, key, fallback) {
  return Object.prototype.hasOwnProperty.call(map, key) ? key : fallback;
}

function Section({
  tone = "paper",
  size = "std",
  panel = false,
  notch = false,
  contour = true,
  id,
  className,
  container = "base",
  innerRef,
  children,
  ...rest
}) {
  const toneKey = TONE_ALIAS[keyOr(TONE_ALIAS, tone, "paper")];
  const sizeKey = keyOr(SIZE_CLASS, size, "std");
  const isBleed = container === "none" || container === false;
  const containerKey = keyOr(CONTAINER_CLASS, container, "base");
  // A paper panel on the paper page would be invisible, so panel/notch only
  // take effect on the dark surfaces.
  const isPanel = Boolean(panel) && toneKey !== "paper";
  const hasNotch = isPanel && Boolean(notch);

  const layer = contour ? (
    <div className={styles.contour} aria-hidden="true">
      <Topo tone={toneKey} variant="static" />
    </div>
  ) : null;

  const body = isBleed ? (
    children
  ) : (
    <div className={cx(styles.container, CONTAINER_CLASS[containerKey])}>{children}</div>
  );

  return (
    <section
      {...rest}
      id={id}
      ref={innerRef}
      data-tone={toneKey}
      data-size={sizeKey}
      data-panel={isPanel ? "true" : undefined}
      className={cx(
        styles.section,
        TONE_CLASS[toneKey],
        SIZE_CLASS[sizeKey],
        isPanel && styles.panel,
        hasNotch && styles.notch,
        className
      )}
    >
      {isPanel ? (
        // The panel needs a clipping box of its own: the notch tab lives
        // OUTSIDE the rounded surface, so the <section> itself cannot clip.
        <div className={styles.surface}>
          {layer}
          {body}
        </div>
      ) : (
        <>
          {layer}
          {body}
        </>
      )}
    </section>
  );
}

/*
 * SectionHead — the ONLY heading block on the site.
 *
 * Renders: micro label → h2 → lead paragraph, with an optional action slot that
 * sits right-aligned on desktop and stacks below on mobile. Always leaves
 * var(--space-9) below itself — that is the site's single heading rhythm, so no
 * section should add its own margin above its content.
 *
 * The <h2> is intentionally NOT restyled here: global h2 owns the condensed-caps
 * type and HeadingWipe (App.js) owns its animation. The one thing styled is an
 * <em> inside the title, which becomes the serif accent word:
 *   title={<>CLIENT <em>WORK</em></>}
 *
 * Props
 *   eyebrow    micro label above the title (string or node) — optional
 *   title      the h2 content, string or React node — optional
 *   titleId    id for the <h2>, so the Section can be aria-labelledby it — optional
 *   lead       supporting paragraph at body size, clamped to 62ch — optional
 *   action     right-hand slot (one CTA / link-arrow) — optional
 *   align      "left" (default) | "center"
 *   className  extra class on the wrapper
 */
export function SectionHead({
  eyebrow,
  title,
  titleId,
  lead,
  action,
  align = "left",
  className
}) {
  if (!eyebrow && !title && !lead && !action) {
    return null;
  }

  const isCenter = align === "center";

  return (
    <div className={cx(styles.head, isCenter && styles.headCenter, className)}>
      <div className={styles.headMain}>
        {eyebrow ? <p className={cx("micro", styles.eyebrow)}>{eyebrow}</p> : null}
        {title ? (
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
        ) : null}
        {lead ? <p className={styles.lead}>{lead}</p> : null}
      </div>
      {action ? <div className={styles.headAction}>{action}</div> : null}
    </div>
  );
}

export default Section;
